import { createEffect, createMemo, createSignal, untrack } from 'solid-js';
import { COVERED, fundOf, issuerOf, mandateOf, name as nameOf, type UniverseEntry } from '~data';
import { matchesPreset, type PresetId } from '~/data/strategies';
import { detail, setDetail } from '~/stores/nav-store';

/**
 * How you got to the fund the detail panel is showing.
 *
 * A trail rather than a single symbol, because a fund of funds is worth opening
 * into. HYLD holds SMAX holds QMVP holds Apple, and following that is the only
 * way to see what you actually own — but the fund two levels down is often not
 * a row on the screener at all (FMAX and QMVP are not in the covered pool), so
 * there would otherwise be no way back to where you started.
 *
 * Seeded from the address bar, so a reload and a shared link both land on the
 * fund they name. Anything unrecognised — or nothing at all — falls back to the
 * first row rather than to an empty panel: a junk symbol in a URL is a typo, not
 * a request for nothing, and a blank half-screen teaches nothing.
 */
const opening = (): string => {
    const named = detail();
    return named != null && fundOf(named) != null ? named : (COVERED[0]?.symbol ?? '');
};

const [trail, setTrail] = createSignal<string[]>([opening()]);

export { trail };

/** The fund on screen — the deepest one drilled into. */
export const selectedSymbol = (): string => trail()[trail().length - 1] ?? '';

/**
 * Keep the selection and the address bar in step, in both directions.
 *
 * Called from the app root rather than run at module scope: an effect created
 * outside a render has no owner and is not flushed until something else forces
 * the queue, so the first change appears to do nothing.
 *
 * Only the fund reached is written — the trail is a path taken, not a place, and
 * a bookmark of it would restore a history nobody asked for.
 */
export const syncUrl = (): void => {
    createEffect(() => setDetail(selectedSymbol() || null));

    // Tracks the hash and nothing else — see the note in compare-store on what
    // reading the selection here does.
    createEffect(() => {
        const named = detail();
        if (named != null && named !== untrack(selectedSymbol) && fundOf(named) != null) setTrail([named]);
    });
};

/** The screener row the trail started from, which stays lit while you are
    deeper so the table still says where this came from. */
export const rootSymbol = (): string => trail()[0] ?? '';

// Whether the look-through panel is open beside the fund panel — the charts of
// what the fund itself holds.
const [holdingsOpen, setHoldingsOpen] = createSignal(false);

export { holdingsOpen };

export const toggleHoldings = (): void => {
    setHoldingsOpen((open) => !open);
};

// Which sector slice is expanded into its constituents, or null for none.
// Cleared when the fund changes: a sector belongs to the fund that published it,
// and "Banks" carried over to a technology fund would resolve to nothing while
// looking like an answer.
const [openSector, setOpenSector] = createSignal<string | null>(null);

export { openSector };

export const toggleSector = (name: string): void => {
    setOpenSector((current) => (current === name ? null : name));
};

export const closeSector = (): void => {
    setOpenSector(null);
};

export const closeHoldings = (): void => {
    setHoldingsOpen(false);
};

/** Picking from the screener starts a fresh trail. The look-through stays open
    if it already was — it is a view of whatever fund is selected, not of the one
    it was opened from, and closing it on every row change would make comparing
    two funds a chore. */
export const setSelectedSymbol = (symbol: string): void => {
    setTrail([symbol]);
    setOpenSector(null);
};

/**
 * Open a fund held by the fund on screen.
 *
 * Revisiting a fund already on the trail walks back to it rather than pushing a
 * second copy — HDIV holds HFIN which holds HMAX, and a trail that could grow
 * HDIV › HFIN › HMAX › HFIN would be a record of clicks rather than of where
 * you are.
 */
export const drillInto = (symbol: string): void => {
    setTrail((current) => {
        const at = current.indexOf(symbol);
        return at === -1 ? [...current, symbol] : current.slice(0, at + 1);
    });
    setOpenSector(null);
};

/** Back to a crumb. */
export const popTo = (index: number): void => {
    setTrail((current) => current.slice(0, index + 1));
    setOpenSector(null);
};

// --- screening ---------------------------------------------------------------

const [query, setQuery] = createSignal('');
const [preset, setPresetSignal] = createSignal<PresetId | null>(null);

export { query, setQuery, preset };

/** Clicking the lit chip clears it — a preset is a filter, not a mode, and
    there should be no separate "All" to hunt for to get back. */
export const setPreset = (next: PresetId): void => {
    setPresetSignal((current) => (current === next ? null : next));
};

export const clearScreen = (): void => {
    setQuery('');
    setPresetSignal(null);
};

/**
 * Every term has to match something, but not the same something.
 *
 * "hamilton util" reads as one fund rather than as a literal string nothing
 * contains, which is how anyone typing into a screener expects it to behave —
 * a plain substring test over the joined text would only be equivalent if
 * people typed the fields in publication order.
 */
const haystack = (entry: UniverseEntry): string =>
    [entry.symbol, nameOf(entry.symbol), mandateOf(entry.symbol), issuerOf(entry.symbol)].filter(Boolean).join(' ').toLowerCase();

/** The screen itself, with the signals lifted out so it can be checked without
    standing up a reactive root. `screened` below is this plus the two signals. */
export const screen = (list: UniverseEntry[], text: string, active: PresetId | null): UniverseEntry[] => {
    const terms = text.toLowerCase().split(/\s+/).filter(Boolean);
    if (active == null && terms.length === 0) return list;

    return list.filter((entry) => {
        if (active != null && !matchesPreset(entry.symbol, active)) return false;
        if (terms.length === 0) return true;
        const haystackText = haystack(entry);
        return terms.every((term) => haystackText.includes(term));
    });
};

export const screened = createMemo<UniverseEntry[]>(() => screen(COVERED, query(), preset()));

/** Whether anything is narrowing the list, for a pane that has to say so. */
export const isScreened = (): boolean => preset() != null || query().trim().length > 0;
